import type { NoyoIssueQueue } from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.types'

export const NOYO_CLASSIFICATION_OPTIONS = [
  { value: 'field_mismatch', label: 'Field mismatch' },
  { value: 'missing_from_platform', label: 'Missing from sureCo' },
  { value: 'missing_from_carrier', label: 'Missing from Noyo' },
  { value: 'unavailable_at_carrier', label: 'Unavailable at carrier' },
] as const

export type NoyoClassification =
  (typeof NOYO_CLASSIFICATION_OPTIONS)[number]['value']

export const DEFAULT_NOYO_ISSUE_CLASSIFICATIONS: NoyoClassification[] = [
  'missing_from_platform',
  'missing_from_carrier',
  'field_mismatch',
  'unavailable_at_carrier',
]

export const DEFAULT_MIN_IMPACT_SCORE = 1

export const NOYO_ISSUE_QUEUE_OPTIONS: ReadonlyArray<{
  value: NoyoIssueQueue
  label: string
}> = [
  { value: 'needs_action', label: 'Needs action' },
  { value: 'policy', label: 'Policy / dates' },
  { value: 'noise', label: 'Noise' },
]

export const DEFAULT_NOYO_ISSUE_QUEUES: NoyoIssueQueue[] =
  NOYO_ISSUE_QUEUE_OPTIONS.map((option) => option.value)

const CLASSIFICATION_VALUES = new Set<string>(
  NOYO_CLASSIFICATION_OPTIONS.map((option) => option.value)
)

const QUEUE_VALUES = new Set<string>(
  NOYO_ISSUE_QUEUE_OPTIONS.map((option) => option.value)
)

export function isNoyoClassification(
  value: string
): value is NoyoClassification {
  return CLASSIFICATION_VALUES.has(value)
}

export function isNoyoIssueQueue(value: string): value is NoyoIssueQueue {
  return QUEUE_VALUES.has(value)
}

export function filterIssuesByQueue<T extends { issueQueue?: string }>(
  rows: readonly T[],
  selectedQueues: readonly string[]
): T[] {
  const allowed = new Set<string>(selectedQueues.filter(isNoyoIssueQueue))
  if (allowed.size === 0 || allowed.size === QUEUE_VALUES.size) {
    return [...rows]
  }
  return rows.filter(
    (row) => typeof row.issueQueue === 'string' && allowed.has(row.issueQueue)
  )
}

export function joinClassifications(selected: readonly string[]): string {
  const allowed = new Set(selected.filter(isNoyoClassification))
  return NOYO_CLASSIFICATION_OPTIONS.map((option) => option.value)
    .filter((value) => allowed.has(value))
    .join(',')
}

export function issueQueueLabel(issueQueue: string | undefined): string {
  const option = NOYO_ISSUE_QUEUE_OPTIONS.find(
    (item) => item.value === issueQueue
  )
  return option?.label ?? '—'
}

export function problemTagColor(
  issueQueue: string | undefined
): 'red' | 'blue' | 'default' {
  if (issueQueue === 'needs_action') {
    return 'red'
  }
  if (issueQueue === 'policy') {
    return 'blue'
  }
  return 'default'
}
